import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useStore } from '../../../../hooks/useStore';
import { Form, Formik } from 'formik';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikFileUpload } from '../../../../components/form-components/common-components/file-upload/FormikFileUpload';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import Popup from '../../../../components/common-components/pop-up/Popup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import styles from './NewExpensePopup.module.css';
import { IExpenseFileResult } from '../../../../models/expense/ExpenseModel';
import { Currency, X, File as FileIcon } from 'lucide-react';

const validationSchema = Yup.object({
  amount: Yup.number()
    .required('Miktar zorunludur')
    .positive("Miktar 0'dan büyük olmalıdır")
    .min(0.01, 'Miktar en az 0.01 olmalıdır'),
  expenseDate: Yup.date().required('Tarih zorunludur'),
  description: Yup.string()
    .required('Açıklama zorunludur')
    .min(3, 'Açıklama en az 3 karakter olmalıdır'),
  files: Yup.array().nullable(),
  status: Yup.string().oneOf(['Pending', 'Approved', 'Rejected']),
});

const EditExpensePopup = () => {
  const { popupStore, expenseStore, authStore } = useStore();
  const [deletedFileIds, setDeletedFileIds] = useState<number[]>([]);
  const [existingFiles, setExistingFiles] = useState<IExpenseFileResult[]>([]);

  useEffect(() => {
    if (expenseStore.selectedExpense) {
      setExistingFiles(expenseStore.selectedExpense.files || []);
      setDeletedFileIds([]);
    }
  }, [expenseStore.selectedExpense]);

  if (!expenseStore.selectedExpense) {
    return null;
  }

  const expense = expenseStore.selectedExpense;
  const initialValues = {
    amount: expense.amount.toString(),
    expenseDate: new Date(expense.expenseDate).toISOString().split('T')[0],
    description: expense.description,
    status: expense.status,
    files: [] as File[],
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const formData = new FormData();
      formData.append('amount', values.amount.toString());
      formData.append('expenseDate', new Date(values.expenseDate).toISOString());
      formData.append('description', values.description);

      if (values.status) {
        formData.append('status', values.status);
      }

      if (deletedFileIds.length > 0) {
        deletedFileIds.forEach(fileId => {
          formData.append('deletedFileIds', fileId.toString());
        });
      }

      if (values.files && values.files.length > 0) {
        values.files.forEach(file => {
          formData.append('files', file);
        });
      }

      const response = await expenseStore.updateExpense(expense.id, formData);

      if (!response.error) {
        popupStore.setIsEditExpenseOpen(false);
        expenseStore.setSelectedExpense(null);
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Harcama kaydı güncellenirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsEditExpenseOpen(false);
    expenseStore.setSelectedExpense(null);
    setDeletedFileIds([]);
    setExistingFiles([]);
  };

  const handleRemoveExistingFile = (fileId: number) => {
    setDeletedFileIds(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const statusOptions = [
    { value: 'Pending', label: 'Beklemede' },
    { value: 'Approved', label: 'Onaylandı' },
    { value: 'Rejected', label: 'Reddedildi' },
  ];

  return (
    <Popup
      isOpen={popupStore.isEditExpenseOpen}
      onClose={handleClose}
      title="Harcama Kaydını Düzenle"
    >
      <div className={styles.container}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="amount"
                  label="Miktar (₺)"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  startAdornment={<Currency size={18} className={styles.currencyIcon} />}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField name="expenseDate" label="Harcama Tarihi" type="date" />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="description"
                  label="Açıklama"
                  placeholder="Harcama açıklamasını giriniz..."
                  type="text"
                />
              </div>

              {authStore.hasAnyRole(['Admin']) && (
                <div className={styles.inputContainer}>
                  <FormikDropdown
                    name="status"
                    label="Durum"
                    placeholder="Durum seçiniz"
                    options={statusOptions}
                  />
                </div>
              )}

              {existingFiles.length > 0 && (
                <div className={styles.inputContainer}>
                  <label className={styles.formLabel}>Mevcut Dosyalar</label>
                  <div className={styles.existingFilesList}>
                    {existingFiles.map((file: IExpenseFileResult) => (
                      <div key={file.id} className={styles.existingFileItem}>
                        <FileIcon size={16} className={styles.fileIcon} />
                        <span className={styles.fileName} title={file.fileName}>
                          {file.fileName}
                        </span>
                        <button
                          type="button"
                          className={styles.removeExistingFileButton}
                          onClick={() => handleRemoveExistingFile(file.id)}
                          title="Kaldır"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.inputContainer}>
                <FormikFileUpload
                  name="files"
                  label="Yeni Dosyalar Ekle (İsteğe Bağlı)"
                  placeholder="Dosya seçin veya buraya sürükleyin"
                  accept=".pdf,.png,.jpeg,.jpg"
                  multiple={true}
                  maxSize={2} // 2MB
                  maxFiles={Math.max(0, 3 - existingFiles.length)} // Remaining slots for new files
                  allowedTypes={['pdf', 'png', 'jpeg', 'jpg']}
                />
                {existingFiles.length >= 3 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    Maksimum dosya sayısına ulaşıldı. Yeni dosya eklemek için mevcut dosyalardan
                    birini kaldırın.
                  </div>
                )}
              </div>

              <div className={styles.buttonContainer}>
                <CommonButton type="secondary" onClick={handleClose}>
                  İptal
                </CommonButton>
                <CommonButton
                  type="primary"
                  disabled={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? 'Güncelleniyor...' : 'Güncelle'}
                </CommonButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Popup>
  );
};

export default observer(EditExpensePopup);
